import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import PropTypes from "prop-types";
import clsx from "clsx";

// Material
import Box from "@material-ui/core/Box";

// Config
import colors from "src/config/colors";
import useTranslate from "src/hooks/useTranslate";

// Components
import Text from "src/components/Text";

const useStyles = makeStyles(theme => ({
  breadcrumb: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(0.5)
  },
  breadcrumbLink: {
    color: colors.riverBed,
    cursor: "pointer",
    "&:hover": {
      color: colors.pictonBlue
    }
  },
  breadcrumbCurrent: {
    color: colors.riverBed,
    fontWeight: "bold"
  },
  breadcrumbSeparator: {
    color: colors.riverBed
  }
}));

// Shows "Step 1/2" for a single-page form (unchanged pre-pagination behavior).
// For a 1.0 template with real multiple pages, shows the titles of pages visited
// so far as a breadcrumb, growing as the user advances via Next — each past page
// is clickable to jump back to it. On the review step, `extraStep` appends a final,
// non-clickable "Review" entry and every page becomes clickable (none is "current").
const PageBreadcrumb = ({ pages, pageIndex, maxPageIndex, onNavigate, extraStep }) => {
  const getText = useTranslate();
  const classes = useStyles();

  if (pages.length <= 1) {
    return (
      <Text type="contentLabel2">
        {extraStep ? extraStep.label : `${getText("statementPage.step")} 1/2`}
      </Text>
    );
  }

  return (
    <Box className={classes.breadcrumb}>
      {pages.slice(0, maxPageIndex + 1).map((page, index) => {
        const isCurrent = !extraStep && index === pageIndex;

        return (
          <React.Fragment key={page.id}>
            {index > 0 && (
              <Text
                type="contentLabel2"
                className={classes.breadcrumbSeparator}
              >
                {">"}
              </Text>
            )}
            <Text
              type="contentLabel2"
              className={clsx(
                isCurrent ? classes.breadcrumbCurrent : classes.breadcrumbLink
              )}
              onClick={!isCurrent ? () => onNavigate(index) : undefined}
            >
              {page.title ?? page.id}
            </Text>
          </React.Fragment>
        );
      })}
      {extraStep && (
        <>
          <Text type="contentLabel2" className={classes.breadcrumbSeparator}>
            {">"}
          </Text>
          <Text type="contentLabel2" className={classes.breadcrumbCurrent}>
            {extraStep.label}
          </Text>
        </>
      )}
    </Box>
  );
};

PageBreadcrumb.propTypes = {
  pages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string
    })
  ).isRequired,
  pageIndex: PropTypes.number.isRequired,
  maxPageIndex: PropTypes.number.isRequired,
  onNavigate: PropTypes.func.isRequired,
  extraStep: PropTypes.shape({
    label: PropTypes.string
  })
};

PageBreadcrumb.defaultProps = {
  extraStep: null
};

export default PageBreadcrumb;
